import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Star } from "lucide-react";
import { useResourceList, useResourceMutations } from "@/hooks/use-admin-resource";
import { AdminPageHeader, RowActions, ResponsiveTableWrap, AdminEmptyState } from "@/components/admin/crud-helpers";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { ImportDefaultsButton } from "@/components/admin/import-defaults-button";
import { buildReviewSeeds } from "@/lib/admin-seeds";

export const Route = createFileRoute("/_authenticated/admin/reviews")({ component: ReviewsAdmin });

type Review = {
  id: string;
  product_id: string;
  user_id: string;
  rating: number;
  title: string | null;
  body: string;
  published: boolean;
  admin_response: string | null;
  created_at: string;
};

function ReviewsAdmin() {
  const { data, isLoading } = useResourceList<Review>("product_reviews");
  const { update, remove } = useResourceMutations("product_reviews");
  const [editing, setEditing] = useState<Review | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "published" | "hidden" | "5" | "1">("all");

  const productIds = useMemo(() => Array.from(new Set((data ?? []).map((r) => r.product_id))), [data]);
  const userIds = useMemo(() => Array.from(new Set((data ?? []).map((r) => r.user_id))), [data]);

  const { data: products = {} } = useQuery({
    enabled: productIds.length > 0,
    queryKey: ["reviews-products", productIds],
    queryFn: async () => {
      const { data, error } = await supabase.from("products").select("id, name").in("id", productIds);
      if (error) throw error;
      return Object.fromEntries((data ?? []).map((p) => [p.id, p.name])) as Record<string, string>;
    },
  });

  const { data: profiles = {} } = useQuery({
    enabled: userIds.length > 0,
    queryKey: ["reviews-profiles", userIds],
    queryFn: async () => {
      const { data, error } = await supabase.from("profiles").select("id, display_name").in("id", userIds);
      if (error) throw error;
      return Object.fromEntries((data ?? []).map((p) => [p.id, p.display_name ?? "Customer"])) as Record<string, string>;
    },
  });

  const rows = useMemo(() => {
    const list = data ?? [];
    const q = search.trim().toLowerCase();
    return list.filter((r) => {
      if (filter === "published" && !r.published) return false;
      if (filter === "hidden" && r.published) return false;
      if (filter === "5" && r.rating < 5) return false;
      if (filter === "1" && r.rating > 2) return false;
      if (!q) return true;
      return [r.title, r.body, products[r.product_id], profiles[r.user_id]]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [data, search, filter, products, profiles]);

  const tabs: { id: typeof filter; label: string }[] = [
    { id: "all", label: `All (${data?.length ?? 0})` },
    { id: "published", label: "Published" },
    { id: "hidden", label: "Hidden" },
    { id: "5", label: "★★★★★" },
    { id: "1", label: "Low (1–2★)" },
  ];

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Product reviews"
        description="Manage customer ratings and feedback. Edit, hide, or delete any review."
        actions={<ImportDefaultsButton table="product_reviews" onConflict="id" loadRows={buildReviewSeeds} />}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search customers, products, review text…"
        count={rows.length}
        countLabel="reviews"
      />

      <div className="flex flex-wrap gap-1.5">
        {tabs.map((t) => (
          <Button key={t.id} size="sm" variant={filter === t.id ? "default" : "outline"} onClick={() => setFilter(t.id)}>
            {t.label}
          </Button>
        ))}
      </div>

      {!isLoading && rows.length === 0 ? (
        <AdminEmptyState title={search || filter !== "all" ? "No reviews match" : "No reviews yet"} />
      ) : (
        <div className="rounded-lg border border-border bg-card">
          <ResponsiveTableWrap>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer / Product</TableHead>
                  <TableHead className="hidden sm:table-cell">Rating</TableHead>
                  <TableHead className="hidden lg:table-cell">Review</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>
                )}
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <div className="font-medium">{profiles[r.user_id] ?? "Customer"}</div>
                      <div className="text-xs text-muted-foreground">{products[r.product_id] ?? "Product"}</div>
                      <div className="mt-1 flex sm:hidden">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} className={cn("h-3 w-3", n <= r.rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40")} />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} className={cn("h-3.5 w-3.5", n <= r.rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40")} />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell max-w-md">
                      {r.title && <div className="font-medium truncate">{r.title}</div>}
                      <div className="text-xs text-muted-foreground line-clamp-2">{r.body}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={r.published ? "default" : "secondary"}>{r.published ? "Live" : "Hidden"}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <RowActions
                        onEdit={() => { setEditing(r); setOpen(true); }}
                        onDelete={() => { if (confirm("Delete this review?")) remove.mutate(r.id); }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ResponsiveTableWrap>
        </div>
      )}

      <ReviewEditDialog
        key={editing?.id ?? "new"}
        open={open}
        onOpenChange={setOpen}
        editing={editing}
        productName={editing ? products[editing.product_id] : undefined}
        customerName={editing ? profiles[editing.user_id] : undefined}
        onSubmit={(values) => {
          if (!editing) return;
          update.mutate({ id: editing.id, values }, { onSuccess: () => setOpen(false) });
        }}
      />
    </div>
  );
}

function ReviewEditDialog({
  open, onOpenChange, editing, onSubmit, productName, customerName,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: Review | null;
  onSubmit: (v: Partial<Review>) => void;
  productName?: string;
  customerName?: string;
}) {
  const [rating, setRating] = useState(editing?.rating ?? 5);
  const [title, setTitle] = useState(editing?.title ?? "");
  const [body, setBody] = useState(editing?.body ?? "");
  const [published, setPublished] = useState(editing?.published ?? true);
  const [response, setResponse] = useState(editing?.admin_response ?? "");

  if (!editing) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit review</DialogTitle>
          <div className="text-xs text-muted-foreground">
            {customerName ?? "Customer"} · {productName ?? "Product"}
          </div>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label>Rating</Label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setRating(n)} className="p-0.5">
                  <Star className={cn("h-6 w-6", rating >= n ? "fill-amber-400 text-amber-400" : "text-muted-foreground/50")} />
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-2"><Label>Title</Label><Input value={title ?? ""} onChange={(e) => setTitle(e.target.value)} /></div>
          <div className="grid gap-2"><Label>Body</Label><Textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} /></div>
          <div className="grid gap-2"><Label>Admin response (optional)</Label><Textarea rows={2} value={response ?? ""} onChange={(e) => setResponse(e.target.value)} placeholder="Public reply shown next to the review" /></div>
          <div className="flex items-center gap-3"><Switch checked={published} onCheckedChange={setPublished} /><Label>Published</Label></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => onSubmit({ rating, title: title?.trim() || null, body, published, admin_response: response?.trim() || null })}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
